export const content = {
  sv: {
    nav: {
      home: "Hem",
      method: "Metoden",
      app: "Appen",
      education: "Utbildning",
      about: "Om LIVON",
    },
    hero: {
      tagline: "Livet som pågår.",
      lead:
        "LIVON är en filosofi och metod för dig som vill leva mer medvetet, balanserat och hållbart — mitt i livet, medan det pågår.",
      ctaMethod: "Utforska metoden",
      ctaApp: "Utforska appen",
      scroll: "Bläddra",
    },
    what: {
      label: "Det här är LIVON",
      title: "Ett sätt att se på människan.",
      intro:
        "LIVON är en filosofi, metod och rörelse som bygger på en enkel men viktig förståelse: människan behöver ses som en helhet.",
      paragraphs: [
        "Vi lever i en tid där människor ofta delas upp i delar. Kroppen behandlas för sig, tankarna för sig, känslorna för sig och prestationerna för sig. Många får råd om hur de borde leva, träna, tänka eller förändras — men få känner sig verkligen förstådda på djupet.",
        "LIVON utgår från att människan är mer komplex än sina symptom, vanor eller prestationer. Hur vi mår och fungerar påverkas av samspelet mellan våra känslor, relationer, erfarenheter, livsvillkor, stressnivåer, hormoner, nervsystem, självkänsla, tankemönster, beteenden och biologiska förutsättningar. Allt hänger samman.",
      ],
      quote:
        "Bakom varje människa finns en historia. Bakom varje beteende finns något som behöver förstås.",
      paragraphsAfter: [
        "Därför handlar LIVON inte om att pressa människor till perfektion eller skapa ännu fler krav. Filosofin bygger istället på förståelse, medvetenhet, balans och hållbar förändring. Målet är inte att människan ska bli någon annan, utan att komma närmare sig själv och skapa ett liv som fungerar både inifrån och ut.",
        "LIVON förenar kunskap från psykologi, beteendevetenskap, hälsa, livsstil, självkännedom och mänsklig utveckling i ett helhetsperspektiv där både kropp, sinne och livssituation får ta plats. Här finns förståelse för att människors utmaningar sällan har en enkel förklaring och att verklig förändring behöver bygga på mer än disciplin och prestation.",
      ],
      audienceLabel: "LIVON är för",
      audience: [
        "människor som vill förstå sig själva djupare",
        "dig som söker hållbarhet och balans i livet",
        "yrkesverksamma som möter andra människor",
        "alla som vill se människan i sin helhet",
      ],
      closing: "Det är inte bara en metod. Det är ett sätt att se på människan.",
      pillars: [
        { k: "Filosofin", v: "Grundtanken. Människan ses som en helhet — komplex, formbar och alltid större än sina delar." },
        { k: "Metoden", v: "Praktiken. Ett ramverk där filosofin omsätts i principer, modeller och evidensgrundade verktyg." },
        { k: "Appen", v: "Vardagen. Det dagliga verktyget för rutiner, reflektion och balans när livet pågår." },
        { k: "Utbildningen", v: "Fördjupningen. Certifieringar och program för dig som vill leva och verka i LIVON-metoden." },
      ],
    },
    planes: {
      label: "Människan i tre plan",
      title: "Body. Mind. Soul.",
      intro:
        "Människan rör sig på tre plan — kroppens, sinnets och själens. LIVON ser dem inte som motsatser eller hierarkier, utan som tre öppna fält som ständigt talar med varandra. När de är i balans pågår livet med lätthet. När de tappar kontakt blir vardagen tung. Det är här arbetet börjar.",
      items: [
        {
          key: "body",
          numeral: "I",
          title: "BODY",
          sv: "Kropp",
          lead: "Det som bär dig genom dagen.",
          body:
            "Det fysiska, det vardagliga, det levda. Kropp, energi, rytm — och den struktur som håller livet ihop när allt annat rör sig.",
          areas: ["02", "04"],
          areasLabel: "Områden inom kroppen",
        },
        {
          key: "mind",
          numeral: "II",
          title: "MIND",
          sv: "Sinne",
          lead: "Det som riktar dig genom livet.",
          body:
            "Det kognitiva och det relationella. Tankar, beteenden, gränser, riktning — det vi väljer, väver och säger till oss själva och varandra.",
          areas: ["03", "05"],
          areasLabel: "Områden inom sinnet",
        },
        {
          key: "soul",
          numeral: "III",
          title: "SOUL",
          sv: "Själ",
          lead: "Det som är du — oavsett vad du gör.",
          body:
            "Det inre, det varande, det stilla. Känslor, närvaro, mening — det djupaste lagret av att vara människa, det som finns kvar när vardagens roller faller.",
          areas: ["01", "06"],
          areasLabel: "Områden inom själen",
        },
      ],
      closing: "Tre plan. Sex områden. Ett liv.",
    },
    areas: {
      label: "Sex livsområden",
      title: "Sex områden. Ett liv.",
      lead:
        "Hela människan består av samma sex områden. Hur de talar med varandra — och hur du arbetar med dem som en helhet — är där LIVON-filosofin blir praktisk.",
      items: [
        { n: "01", t: "Inre liv", d: "Känslor, stress, självkännedom, självbild och psykiskt välmående." },
        { n: "02", t: "Kropp & hälsa", d: "Sömn, återhämtning, rörelse, kost, hormoner och energi." },
        { n: "03", t: "Relationer", d: "Kommunikation, gränser, anknytning, familj och social trygghet." },
        { n: "04", t: "Struktur & vardag", d: "Rutiner, balans, planering, livsstruktur och hållbara vanor." },
        { n: "05", t: "Mål & riktning", d: "Motivation, mening, utveckling, arbete och framtid." },
        { n: "06", t: "Lugn & livskvalitet", d: "Närvaro, återhämtning, livsglädje och ett hållbart liv." },
      ],
    },
    method: {
      label: "Livon-metoden",
      title: "Filosofin, översatt till en metod.",
      body:
        "Ett ramverk av principer, modeller och övningar för dig som vill arbeta med LIVON i praktiken — privat eller professionellt.",
      cta: "Läs mer på method.livon.se",
    },
    appSec: {
      label: "Livon-appen",
      title: "Filosofin, i din vardag.",
      body:
        "Appen är där LIVON möter dagen. Överblick, rutiner och balans när livet pågår som mest.",
      cta: "Öppna app.livon.se",
    },
    education: {
      label: "Utbildningar",
      title: "En egen disciplin — formad kring människan som helhet.",
      body:
        "LIVON Academy fördjupar filosofin och metoden till en levande disciplin — genom kurser, program och certifieringar.",
      cta: "Utforska utbildningarna",
    },
    offerings: {
      label: "Vägar in",
      title: "En filosofi. Tre vägar in.",
      lead:
        "LIVON möter dig där du är — i din vardag, i din yrkesroll eller i din fördjupning. Tre olika ingångar till samma helhetssyn.",
    },
    about: {
      label: "Om LIVON",
      eyebrow: "Manifestet",
      title: "Mitt i allt — börjar livet.",
      paragraphs: [
        "LIVON är inte ett varumärke som vill göra dig till en bättre version av dig själv. LIVON är en inbjudan att vända sig inåt — hem till sig själv, mitt i det liv du redan lever.",
        "Vi har vant oss vid att vänta. Vänta tills vi är klara med en utbildning, en relation, en kris, en flytt. Vänta tills vi hittar tid att andas. Vänta tills livet känns ordentligt. Men livet är inte något som börjar någon annanstans. Det pågår redan — hela tiden, mitt i det vi gör.",
        "LIVON väljer en annan väg. En lugn väg. Där balans är viktigare än perfektion, riktning är viktigare än fart, och livsglädje är ett resultat av medvetna val — inte tur.",
        "Det börjar med en enkel fråga: hur vill du faktiskt leva — nu, idag, mitt i allt? Resten är LIVON.",
      ],
      signature: "— LIVON. Livet som pågår.",
    },
    footer: {
      tagline: "Livet som pågår.",
      sections: {
        product: "Produkter",
        company: "LIVON",
      },
      links: {
        method: "Metoden",
        app: "Appen",
        education: "Utbildning",
        about: "Om LIVON",
        contact: "Kontakt",
      },
      copyright: "© LIVON. Alla rättigheter förbehållna.",
    },
  },
  en: {
    nav: {
      home: "Home",
      method: "Method",
      app: "App",
      education: "Education",
      about: "About",
    },
    hero: {
      tagline: "Life, as it happens.",
      lead:
        "LIVON is a philosophy and method for those who want to live more consciously, in balance and sustainably — right in the middle of life, while it is unfolding.",
      ctaMethod: "Explore the method",
      ctaApp: "Explore the app",
      scroll: "Scroll",
    },
    what: {
      label: "This is LIVON",
      title: "A way of seeing the human being.",
      intro:
        "LIVON is a philosophy, a method and a movement built on a simple but vital understanding: the human being must be seen as one whole.",
      paragraphs: [
        "We live in a time when people are often split into parts. The body is treated separately, thoughts separately, emotions separately, performance separately. Many are given advice on how they should live, train, think or change — but few feel truly understood at a deeper level.",
        "LIVON begins from the conviction that the human being is more complex than her symptoms, habits or performance. How we feel and function is shaped by the interplay between emotions, relationships, experiences, life circumstances, stress levels, hormones, nervous system, self-esteem, thought patterns, behaviours and biological conditions. Everything is connected.",
      ],
      quote:
        "Behind every human, there is a story. Behind every behaviour, something that needs to be understood.",
      paragraphsAfter: [
        "That is why LIVON is not about pressing people toward perfection or creating yet more demands. The philosophy rests on understanding, awareness, balance and sustainable change. The goal is not to become someone else, but to come closer to yourself — and to create a life that works from the inside out.",
        "LIVON brings together knowledge from psychology, behavioural science, health, lifestyle, self-knowledge and human development into one holistic perspective — where body, mind and life situation all have a place. Here there is room for the understanding that human challenges rarely have a simple explanation, and that real change is built on more than discipline and performance.",
      ],
      audienceLabel: "LIVON is for",
      audience: [
        "people who want to understand themselves more deeply",
        "those seeking sustainability and balance in life",
        "professionals who meet other human beings",
        "anyone who wants to see the human as one whole",
      ],
      closing: "It is not just a method. It is a way of seeing the human being.",
      pillars: [
        { k: "The Philosophy", v: "The core idea. The human being is seen as one whole — complex, shapeable and always greater than her parts." },
        { k: "The Method", v: "The practice. A framework where the philosophy becomes principles, models and evidence-based tools." },
        { k: "The App", v: "The everyday. The daily tool for routines, reflection and balance, while life is unfolding." },
        { k: "The Education", v: "The depth. Certifications and programs for those who want to live and work within the LIVON Method." },
      ],
    },
    planes: {
      label: "The human on three planes",
      title: "Body. Mind. Soul.",
      intro:
        "The human moves on three planes — the body, the mind and the soul. LIVON sees them not as opposites or hierarchies, but as three open fields in constant conversation with each other. When they are in balance, life flows with ease. When they lose contact, the everyday becomes heavy. This is where the work begins.",
      items: [
        {
          key: "body",
          numeral: "I",
          title: "BODY",
          sv: "The body",
          lead: "What carries you through the day.",
          body:
            "The physical, the everyday, the lived. Body, energy, rhythm — and the structure that holds life together when everything else is moving.",
          areas: ["02", "04"],
          areasLabel: "Areas within the body",
        },
        {
          key: "mind",
          numeral: "II",
          title: "MIND",
          sv: "The mind",
          lead: "What gives you direction through life.",
          body:
            "The cognitive and the relational. Thoughts, behaviours, boundaries, direction — what we choose, weave and say to ourselves and each other.",
          areas: ["03", "05"],
          areasLabel: "Areas within the mind",
        },
        {
          key: "soul",
          numeral: "III",
          title: "SOUL",
          sv: "The soul",
          lead: "What is you — no matter what you do.",
          body:
            "The inner, the being, the still. Emotions, presence, meaning — the deepest layer of being human, the part that remains when daily roles fall away.",
          areas: ["01", "06"],
          areasLabel: "Areas within the soul",
        },
      ],
      closing: "Three planes. Six areas. One life.",
    },
    areas: {
      label: "Six life areas",
      title: "Six areas. One life.",
      lead:
        "The whole human being is made of the same six areas. How they speak with each other — and how you work with them as one whole — is where the LIVON philosophy becomes practical.",
      items: [
        { n: "01", t: "Inner Life", d: "Emotions, stress, self-awareness, self-image and mental wellbeing." },
        { n: "02", t: "Body & Health", d: "Sleep, recovery, movement, nutrition, hormones and energy." },
        { n: "03", t: "Relationships", d: "Communication, boundaries, attachment, family and social safety." },
        { n: "04", t: "Structure & Everyday", d: "Routines, balance, planning, life structure and sustainable habits." },
        { n: "05", t: "Goals & Direction", d: "Motivation, meaning, growth, work and future." },
        { n: "06", t: "Calm & Quality of Life", d: "Presence, recovery, joy and a sustainable life." },
      ],
    },
    method: {
      label: "The LIVON Method",
      title: "The philosophy, translated into a method.",
      body:
        "A framework of principles, models and practices for those who want to work with LIVON — privately or professionally.",
      cta: "Read more at method.livon.se",
    },
    appSec: {
      label: "The LIVON App",
      title: "The philosophy, in your everyday.",
      body:
        "The app is where LIVON meets the day. Perspective, routines and balance when life is at its busiest.",
      cta: "Open app.livon.se",
    },
    education: {
      label: "Education",
      title: "A discipline of its own — shaped around the human as one whole.",
      body:
        "LIVON Academy deepens the philosophy and the method into a living discipline — through courses, programs and certifications.",
      cta: "Explore the programs",
    },
    offerings: {
      label: "Ways in",
      title: "One philosophy. Three ways in.",
      lead:
        "LIVON meets you where you are — in your everyday, in your professional role or in your deeper study. Three doors into the same holistic view.",
    },
    about: {
      label: "About LIVON",
      eyebrow: "The Manifesto",
      title: "Right in the middle of everything — life begins.",
      paragraphs: [
        "LIVON is not a brand trying to make you a better version of yourself. LIVON is an invitation to turn inward — home to yourself, in the middle of the life you are already living.",
        "We have grown used to waiting. Waiting until we finish the degree, the relationship, the crisis, the move. Waiting until we find time to breathe. Waiting until life feels proper. But life is not something that begins somewhere else. It is already happening — all the time, right inside what we are doing.",
        "LIVON chooses a different path. A calm one. Where balance matters more than perfection, direction matters more than speed, and the joy of living is the result of conscious choices — not luck.",
        "It begins with a simple question: how do you actually want to live — now, today, right in the middle of everything? The rest is LIVON.",
      ],
      signature: "— LIVON. Life, as it happens.",
    },
    footer: {
      tagline: "Life, as it happens.",
      sections: {
        product: "Products",
        company: "LIVON",
      },
      links: {
        method: "Method",
        app: "App",
        education: "Education",
        about: "About",
        contact: "Contact",
      },
      copyright: "© LIVON. All rights reserved.",
    },
  },
};
